import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";

// Formulaire de contact 2.0 — câblé sur le CRM live (POST /api/contact-messages).
// Le endpoint attend { full_name, phone, email, message }.
// On replie profil / type d'énergie / société dans le message pour ne rien perdre.
const inputCls =
  "w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";

export default function LeadFormV2({ source = "contact", requestType = "etude" }) {
  const [profile, setProfile] = useState("particulier");
  const [energy, setEnergy] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);

  const isPro = profile === "professionnel";

  async function onSubmit(e) {
    e.preventDefault();
    if (!consent) {
      toast.error("Merci d'accepter la politique de confidentialité.");
      return;
    }
    const f = e.currentTarget;
    const full_name = f.full_name.value.trim();
    const phone = f.phone.value.trim();
    const email = f.email.value.trim();
    const userMsg = (f.message.value || "").trim();
    setLoading(true);

    // Dépôt de facture : note + (pour une image) extraction IA des données clés
    const billLines = [];
    if (file) {
      billLines.push(`Facture déposée : ${file.name} (${Math.round(file.size / 1024)} Ko)`);
      const imgTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      if (imgTypes.includes(file.type) && file.size <= 8 * 1024 * 1024) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const r = await api.post("/bills/analyze", fd);
          const d = r?.data || {};
          if (!d.error) {
            const extr = [
              d.supplier_name ? `Fournisseur : ${d.supplier_name}` : null,
              d.energy_type ? `Énergie : ${d.energy_type}` : null,
              d.annual_kwh ? `Conso annuelle : ${d.annual_kwh} kWh` : null,
              d.current_price_kwh ? `Prix du kWh : ${d.current_price_kwh} €` : null,
            ].filter(Boolean);
            if (extr.length) billLines.push("Données extraites de la facture :", ...extr);
          }
        } catch (_) {
          // analyse best-effort — n'empêche pas l'envoi
        }
      }
    }

    // Compose un message riche pour le CRM (le endpoint ne stocke qu'un champ message)
    const parts = [
      `Type de demande : ${requestType === "facture" ? "Dépôt de facture" : "Étude gratuite"} (source: ${source})`,
      `Profil : ${isPro ? "Professionnel" : "Particulier"}`,
      energy ? `Énergie : ${energy}` : null,
      isPro && f.company ? `Société : ${f.company.value || "—"}` : null,
      isPro && f.sector ? `Secteur : ${f.sector.value || "—"}` : null,
      phone ? `Téléphone : ${phone}` : null,
      ...billLines,
      "",
      userMsg || "(Pas de message saisi — demande d'étude / dépôt de facture.)",
    ].filter((x) => x !== null);
    const message = parts.join("\n");

    try {
      await api.post("/contact-messages", { full_name, phone, email, message });
      setDone(true);
      toast.success("Votre demande a bien été reçue.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Envoi impossible. Réessayez."
      );
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-3xl border border-border bg-card p-8 text-center">
        <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
        <h3 className="font-display text-2xl font-bold text-primary mt-4">Votre demande a bien été reçue</h3>
        <p className="text-muted-foreground mt-2">
          Notre équipe revient vers vous rapidement. Vous recevrez une confirmation par e-mail.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-3xl border border-border bg-card p-6 md:p-8 space-y-5">
      <div>
        <label className="text-sm font-semibold text-primary">Je suis</label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {[["particulier", "Particulier"], ["professionnel", "Professionnel"]].map(([val, label]) => (
            <button
              type="button"
              key={val}
              onClick={() => setProfile(val)}
              className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                profile === val
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-card hover:bg-secondary"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Nom et prénom *</label>
          <input name="full_name" required minLength={2} className={inputCls} placeholder="Jean Dupont" />
        </div>
        <div>
          <label className="text-sm font-medium">Téléphone</label>
          <input name="phone" className={inputCls} placeholder="06 12 34 56 78" />
        </div>
        <div>
          <label className="text-sm font-medium">E-mail *</label>
          <input name="email" type="email" required className={inputCls} placeholder="jean.dupont@email.fr" />
        </div>
        <div>
          <label className="text-sm font-medium">Énergie concernée</label>
          <select value={energy} onChange={(e) => setEnergy(e.target.value)} className={inputCls}>
            <option value="">— Sélectionner —</option>
            <option value="Électricité">Électricité</option>
            <option value="Gaz">Gaz</option>
            <option value="Électricité + Gaz">Électricité + Gaz</option>
          </select>
        </div>
      </div>

      {isPro && (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Société</label>
            <input name="company" className={inputCls} placeholder="Nom de l'entreprise" />
          </div>
          <div>
            <label className="text-sm font-medium">Secteur</label>
            <input name="sector" className={inputCls} placeholder="Ex. commerce, industrie…" />
          </div>
        </div>
      )}

      <div>
        <label className="text-sm font-medium">Votre message</label>
        <textarea name="message" rows={4} className={inputCls} placeholder="Décrivez votre besoin (facultatif)…" />
      </div>

      <div>
        <label className="text-sm font-medium">
          Votre facture {requestType === "facture" ? "" : "(facultatif)"}
        </label>
        <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-input bg-secondary/40 px-4 py-3 text-sm transition-colors hover:bg-secondary">
          <Upload className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <span className={file ? "font-medium text-primary" : "text-muted-foreground"}>
            {file ? file.name : "PDF, JPG ou PNG — cliquez pour choisir votre facture"}
          </span>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>
        {file && (
          <button
            type="button"
            onClick={() => setFile(null)}
            className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
          >
            <X className="h-3 w-3" /> Retirer le fichier
          </button>
        )}
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          Astuce : une photo nette de votre facture permet une pré-analyse automatique des informations clés.
        </p>
      </div>

      <label className="flex items-start gap-3 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1"
        />
        <span>
          J'accepte que mes données soient utilisées pour traiter ma demande, conformément à la
          politique de confidentialité.
        </span>
      </label>

      <Button
        type="submit"
        disabled={loading}
        className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi…
          </>
        ) : (
          "Envoyer ma demande"
        )}
      </Button>
    </form>
  );
}
